

import React, { useState, useEffect, useContext } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  ArrowLeft, Eye, CheckCircle, ShieldCheck, XCircle,
  Trash2, MapPin, Home, Tag
} from 'lucide-react';
import { FiSearch, FiX } from 'react-icons/fi';
import '../../../assets/css/admin/dashboard/adminsellers/sellerproperties.css';
import AdminMasterLayout from 'layouts/AdminMasterLayout';
import AppContext from 'context/AppContext';
import PropertyViewScreen from 'subscreens/Seller/viewscreen/PropertyViewScreen';

/* ============================================================
   CONFIRM MODAL COMPONENT
   ============================================================ */
function ConfirmModal({ isOpen, onClose, onConfirm, title, message, confirmText, confirmColor = '#EF4444', icon }) {
  if (!isOpen) return null;
  return (
    <div className="sp-confirm-overlay" onClick={onClose}>
      <div className="sp-confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="sp-confirm-icon-wrap" style={{ backgroundColor: `${confirmColor}18` }}>
          {icon}
        </div>
        <h3 className="sp-confirm-title">{title}</h3>
        <p className="sp-confirm-message">{message}</p>
        <div className="sp-confirm-actions">
          <button className="sp-confirm-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="sp-confirm-action-btn"
            style={{ backgroundColor: confirmColor }}
            onClick={() => { onConfirm(); onClose(); }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ============================================================
   MAIN COMPONENT — SellerProperties
   ============================================================ */
const SellerProperties = () => {
  const { sellerId } = useParams();
  const location = useLocation();

  const { authToken, Navigate, baseUrl, baseUrlForFiles } = useContext(AppContext);

  const seller = location.state?.seller;

  const [properties, setProperties] = useState(seller?.exproperties || []);
  const [search, setSearch] = useState('');
  const [viewingProp, setViewingProp] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tableLoading, setTableLoading] = useState(false);
  const [animateCards, setAnimateCards] = useState(false);

  useEffect(() => {
    setAnimateCards(true);
  }, []);



  /* ── Filtering ── */
  const getTypeColor = t => t === 'FOR SALE' ? '#10B981' : '#17a2b8';
  const getStatusColor = s => {
    if (s === 'Active') return '#10B981';
    if (s === 'Sold' || s === 'Rented') return '#8b5cf6';
    if (s === 'Rejected' || s === 'rejected') return '#ef4444';
    if (s === 'Pending' || s === 'pending') return '#f59e0b';
    return '#EF4444';
  };

  const filtered = properties.filter(p => {
    const q = search.toLowerCase();
    const matchQ = !q || p.property_title?.toLowerCase().includes(q) || p.location?.toLowerCase().includes(q) || p.full_address?.toLowerCase().includes(q);
    const matchT = typeFilter === 'all' || p.listing_type?.toLowerCase() === typeFilter || p.listingType?.toLowerCase() === typeFilter;
    const matchS = statusFilter === 'all' || p.status?.toLowerCase() === statusFilter;
    return matchQ && matchT && matchS;
  });


  /* ── Stats ── */
  const rentCount = properties.filter(p => (p.listing_type || p.listingType) === 'FOR RENT' || (p.listing_type || p.listingType) === 'rent').length;
  const saleCount = properties.filter(p => (p.listing_type || p.listingType) === 'FOR SALE' || (p.listing_type || p.listingType) === 'sell').length;
  const soldCount = properties.filter(p => p.status?.toLowerCase() === 'sold').length;

  const statsData = [
    { label: 'Total Properties', value: properties.length, color: '#8b5cf6', bg: '#ede9fe' },
    { label: 'For Rent', value: rentCount, color: '#3b82f6', bg: '#dbeafe' },
    { label: 'For Sale', value: saleCount, color: '#10b981', bg: '#d1fae5' },
    { label: 'Sold', value: soldCount, color: '#8b5cf6', bg: '#ede9fe' },
  ];

  const handleSuspendProperty = (property) => {
    const newStatus =
      property.status === "Active"
        ? "Suspended"
        : "Active";

    Swal.fire({
      title: `${newStatus === "Suspended" ? "Suspend" : "Activate"} Property?`,
      text: `Are you sure you want to ${newStatus === "Suspended" ? "suspend" : "activate"
        } this property?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#000000",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Continue"
    }).then((result) => {

      if (!result.isConfirmed) return;

      axios.post(
        `${baseUrl}/admin/sellers/changePropertySuspend`,
        {
          id: property.id,
          status: newStatus
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`
          }
        }
      )
        .then((response) => {

          if (response.data.success === true) {

            Swal.fire(
              "Success",
              `Property ${newStatus === "Suspended"
                ? "suspended"
                : "activated"
              } successfully.`,
              "success"
            );

            // Update local property list
            setProperties((prev) =>
              prev.map((item) =>
                item.id === property.id
                  ? { ...item, status: newStatus }
                  : item
              )
            );

            // Update currently opened property also
            setViewingProp((prev) =>
              prev
                ? { ...prev, status: newStatus }
                : prev
            );
          }
        })
        .catch((error) => {

          console.log(error);

          Swal.fire(
            "Error",
            "Something went wrong while updating property status.",
            "error"
          );
        });
    });
  };
  const handleSoldProperty = (property) => {

    const listingType =
      property.listingType || property.listing_type;

    const status =
      listingType === "FOR SALE" ||
        listingType === "Sale"
        ? "Sold"
        : "Rented";

    Swal.fire({
      title: `Mark Property as ${status}?`,
      text: `Are you sure you want to mark this property as ${status.toLowerCase()}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#000000",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Mark"
    }).then((result) => {
      if (!result.isConfirmed) return;
      axios.post(
        `${baseUrl}/admin/sellers/markPropertySold`,
        {
          id: property.id,
          status: status
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`
          }
        }
      )
        .then((response) => {
          if (response.data.success === true) {
            Swal.fire(
              "Success",
              `Property marked as ${status.toLowerCase()} successfully.`,
              "success"
            );
            setProperties((prev) =>
              prev.map((item) =>
                item.id === property.id
                  ? { ...item, status: status }
                  : item
              )
            );
            setViewingProp((prev) =>
              prev
                ? { ...prev, status: status }
                : prev
            );
          }
        })
        .catch((error) => {
          console.log(error);
          Swal.fire(
            "Error",
            `Unable to mark property as ${status.toLowerCase()}.`,
            "error"
          );
        });
    });
  };
  
  const deleteProperty = (property) => {

    Swal.fire({
      title: "Delete Property?",
      text: "Are you sure you want to delete this property? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#000000",
      confirmButtonText: "Yes, Delete"
    }).then((result) => {

      if (!result.isConfirmed) return;

      axios.post(`${baseUrl}/admin/sellers/deleteProperty`,
        {
          data: {
            id: property.id
          },
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        }
      )
        .then((response) => {
          if (response.data.success === true) {
            Swal.fire(
              "Deleted",
              "Property deleted successfully.",
              "success"
            );
            // Remove property from list
            setProperties((prev) =>
              prev.filter((item) => item.id !== property.id)
            );
            // Close view screen
            setViewingProp(null);
          }
        })
        .catch((error) => {
          console.log(error);
          Swal.fire(
            "Error",
            "Unable to delete property.",
            "error"
          );
        });
    });
  };
  /* ── VIEW SCREEN ── */
  if (viewingProp) {
    return (
      <PropertyViewScreen
        property={viewingProp}
        isAdmin={true}
        onBack={() => setViewingProp(null)}
        onToggle={handleSuspendProperty}
        onSold={handleSoldProperty}
        onDelete={deleteProperty}
      />
    );
  }


  /* ── MAIN RENDER ── */
  return (
    <AdminMasterLayout>
      <div className="sp-page">

        {/* ── Header ── */}
        <div className="sp-header">
          <button className="sp-back-btn" onClick={() => Navigate('/admin/sellers')}>
            <ArrowLeft size={16} />
            Back to Sellers
          </button>

          <div className="sp-header-info">
            <div className="sp-header-seller">
              <img
                src={`${baseUrlForFiles}${seller?.profileImage}`}
                alt={`${seller?.firstName} ${seller?.lastName}`}
                className="sp-header-avatar"
              />
              <div>
                <h1 className="sp-header-title">
                  {seller.firstName} {seller.lastName}'s Properties
                </h1>
                <p className="sp-header-sub">
                  {seller.sellerId} · {seller.email}
                </p>
              </div>
              <span
                className="sp-header-status-badge"
                style={{ background: seller.statusColor, color: seller.statusTextColor }}
              >
                {seller.status}
              </span>
            </div>
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="sp-stats-row">
          {statsData.map((s, i) => (
            <div
              key={s.label}
              className="sp-stat-card"
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div
                className="sp-stat-icon-circle"
                style={{ background: s.bg, color: s.color }}
              >
                <Home size={20} />
              </div>
              <div className="sp-stat-details">
                <span className="sp-stat-val">{s.value}</span>
                <span className="sp-stat-label">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filters ── */}
        <div className="sp-filters">
          <div className="sp-search-wrap">
            <FiSearch className="sp-search-icon" />
            <input
              className="sp-search-input"
              placeholder="Search properties…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="sp-search-clear" onClick={() => setSearch('')}>
                <FiX size={12} />
              </button>
            )}
          </div>
        </div>

        {/* ── Properties Table (MyProperty style) ── */}
        <div className="sp-table-container">
          <div className="sp-table-wrap">
            <table className="sp-table">
              <thead>
                <tr>
                  <th>IMAGE</th>
                  <th>TITLE & PRICE</th>
                  <th>TYPE</th>
                  <th>LOCATION</th>
                  <th>STATUS</th>
                  <th style={{ textAlign: 'right' }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <tr>
                    <td colSpan="6">
                      <div className="sp-table-loader">
                        <div className="sp-table-loader-spinner"></div>
                        <h3>Loading Properties...</h3>
                        <p>Please wait while data is loading</p>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan="6">
                      <div className="sp-empty-state">
                        <Home size={48} />
                        <h3>No properties found</h3>
                        <p>Try adjusting your search or filters</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((property, idx) => {
                    const propId = property.id || property.property_id;
                    const typeColor = getTypeColor(property.listingType || property.listing_type);
                    const statusColor = getStatusColor(property.status);
                    console.log("Property:", property);
                    console.log("Images:", property?.expropertyimages);
                    console.log("ExProperty:", property.exproperty);
                    // const firstImage  = property.expropertyimages?.length > 0
                    //   ? `${baseUrlForFiles}/${property.expropertyimages[0].images}`
                    //   : 'https://via.placeholder.com/400x300?text=No+Image';

                    return (
                      <tr
                        key={propId}
                        style={{ animationDelay: `${idx * 0.05}s` }}
                      >
                        {/* Image */}
                        <td>
                          <img
                            src={`${baseUrlForFiles}/${property.expropertyimages[0]?.images}`}
                            alt={property.property_title}
                            className="sp-property-image"
                          />
                        </td>

                        {/* Title & Price */}
                        <td>
                          <div className="sp-property-title-section">
                            <h3 className="sp-property-title">{property.property_title}</h3>
                            <p className="sp-property-price">{property.price}</p>
                            {property.description && (
                              <p className="sp-property-description">
                                {property.description.substring(0, 50)}...
                              </p>
                            )}
                          </div>
                        </td>

                        {/* Listing Type */}
                        <td>
                          <span
                            className="sp-property-type-badge"
                            style={{ backgroundColor: `${typeColor}20`, color: typeColor }}
                          >
                            {property.listingType || property.listing_type}
                          </span>
                        </td>

                        {/* Location */}
                        <td>
                          <span className="sp-property-location">
                            {property.location || property.city || '—'}
                          </span>
                        </td>

                        {/* Status */}
                        <td>
                          <span
                            className="sp-property-status-badge"
                            style={{ backgroundColor: `${statusColor}20`, color: statusColor }}
                          >
                            {property.status}
                          </span>
                        </td>

                        {/* View Button */}
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="sp-table-view-btn"
                            onClick={() =>
                              setViewingProp({ ...property, id: propId, typeColor, statusColor })
                            }
                          >
                            <Eye size={14} />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {filtered.length > 0 && (
            <div className="sp-table-footer">
              Showing {filtered.length} of {properties.length} properties
            </div>
          )}
        </div>

      </div>
    </AdminMasterLayout>
  );
};

export default SellerProperties;